import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Drawer, Box, Typography, IconButton, Chip, Divider, TextField,
  Button, Avatar, Select, MenuItem, FormControl, InputLabel, FormHelperText,
  Tab, Tabs, CircularProgress, Tooltip, Badge, Stack, Paper, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import LeadNumberBadge from "./LeadNumberBadge";
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Event as EventIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Send as SendIcon,
  AutoAwesome as AiIcon,
  Note as NoteIcon,
  History as HistoryIcon,
  Flag as FlagIcon,
  Star as StarIcon,
  MeetingRoom as HallIcon,
  OpenInNew as OpenInNewIcon,
  Warning as WarningIcon,
  Call as CallIcon,
  MailOutline as MailOutlineIcon,
  EventAvailable as EventAvailableIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  EditNote as EditNoteIcon,
  SwapHoriz as SwapHorizIcon,
  PersonAddAlt as PersonAddAltIcon,
} from "@mui/icons-material";
import {
  fetchLeadThread, updateLead, addLeadNote, deleteLeadNote,
  fetchAiSuggestions, fetchTeamUsers, fetchLeadEmails,
  fetchRelatedLeads, markLeadAsPrimary, flagLeadSameEvent, trashLead, restoreLead,
  type LeadThread, type LeadNote, type LeadActivity, type LeadEmailRow,
  type AiSuggestion, type TeamUser, type LeadStatus,
  STATUS_CONFIG, AI_SCORE_CONFIG, PRIORITY_CONFIG,
} from "./leadApi";
import { formatEventDate, formatLeadBudget } from "./leadCellUtils";
import { leadConfig, showsVenueField, resolveInquirySubject } from "./leadVertical";
import { formatDateTime, formatShortDate, formatRelativeTime } from "../../utils/dateFormat";
import { useLeadBus } from "../../hooks/useLeadBus";
import LeadActivityTimeline from "./LeadActivityTimeline";
import { dedupeLeadEmails } from "./dedupeLeadEmails";
import CustomerRelationshipPanel from "./CustomerRelationshipPanel";
import ContactLogPanel from "./ContactLogPanel";
import { usePermission } from "../../hooks/usePermission";

/* ── Design tokens ─────────────────────────────────────────────────────── */
const NAVY = "#16233A";
const GOLD = "#7C4DFF";
const DRAWER_WIDTH = 720;

interface Props {
  open: boolean;
  threadId: number | null;
  onClose: () => void;
  onUpdated?: () => void;
}

export default function LeadThreadDrawer({ open, threadId, onClose, onUpdated }: Props) {
  const navigate = useNavigate();
  const [thread, setThread] = useState<LeadThread | null>(null);
  const [loading, setLoading] = useState(false);
  // Surfaces a real error so the drawer never falls into a silent "Lead
  // not found" again when the API returns 500 / 404 / network-error.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tab, setTab] = useState(0);
  const [noteText, setNoteText] = useState("");
  const [leadEmails, setLeadEmails] = useState<LeadEmailRow[]>([]);
  const [leadEmailsLoaded, setLeadEmailsLoaded] = useState(false);
  const [suggestions, setSuggestions] = useState<AiSuggestion[]>([]);
  const [teamUsers, setTeamUsers] = useState<TeamUser[]>([]);
  const [saving, setSaving] = useState(false);

  // Customer-context state — populates the new "Customer Status" panel.
  // Empty array = "no other leads on this customer"; the section hides.
  const [relatedLeads, setRelatedLeads] = useState<LeadThread[]>([]);
  const { isAdmin, canEdit } = usePermission();
  const canManageResolution = isAdmin || canEdit("leads");

  // Monotonically incrementing counter — each load() call gets a unique id.
  // Stale responses (from superseded fetches) are silently discarded.
  const loadSeqRef = useRef(0);

  const load = useCallback(async () => {
    // Defensive: reject anything that isn't a positive integer. Prevents the
    // drawer from endless-loading on "task-12", undefined, NaN, etc.
    if (!threadId || typeof threadId !== "number" || !Number.isInteger(threadId) || threadId <= 0) {
      // eslint-disable-next-line no-console
      console.error("Invalid lead ID passed to drawer", threadId);
      setLoadError("Invalid lead reference");
      setLoading(false);
      return;
    }
    const seq = ++loadSeqRef.current;

    // Clear immediately so no stale data shows while the request is in-flight
    setThread(null);
    setSuggestions([]);
    setLoading(true);
    setLoadError(null);

    try {
      const [t, ai, users, related] = await Promise.all([
        fetchLeadThread(threadId),
        fetchAiSuggestions(threadId).catch(() => []),
        fetchTeamUsers().catch(() => []),
        // Customer-context: every other lead on the same customer (incl.
        // archived / merged so the operator can see the full picture).
        fetchRelatedLeads(threadId).catch(() => [] as LeadThread[]),
      ]);
      // Discard if a newer load has already started (fast lead switching)
      if (seq !== loadSeqRef.current) return;
      setThread(t);
      setSuggestions(ai);
      setTeamUsers(users);
      // Defensive filter — never let the drawer's lead appear inside its
      // own Related Enquiries list, regardless of what the API returned.
      setRelatedLeads(related.filter((r) => r.id !== threadId));
    } catch (e: any) {
      if (seq !== loadSeqRef.current) return;
      // Surface the error instead of silently falling through to the empty
      // state — the previous swallow masked a real backend SQL bug
      // (latestOfMany column ambiguity on lead_contacts.task_id).
      // eslint-disable-next-line no-console
      console.error("Lead fetch failed", { threadId, error: e });
      setLoadError(e?.message ?? "Failed to load lead.");
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [threadId]);

  useEffect(() => {
    if (open && threadId) {
      load();
      setTab(0);
    }
    if (!open) {
      // Reset when drawer closes so reopening never flashes a previous lead
      setThread(null);
      setSuggestions([]);
    }
  }, [open, threadId, load]);

  /* Live sync — if the lead currently shown in the drawer is mutated
     elsewhere (the leads table behind the drawer, the pipeline, another
     tab) refetch so the operator sees the fresh state. Events for
     OTHER leads are ignored. */
  useLeadBus((ev) => {
    if (!open || !threadId) return;
    if (ev.leadId === threadId || ev.leadId === 0) {
      load();
      // A new activity almost always means a new email — invalidate
      // the Emails tab cache too so re-opening the tab shows the row.
      if (ev.type === "lead.activity" || ev.type === "lead.refresh-all") {
        setLeadEmailsLoaded(false);
      }
    }
  });

  // Emails tab — server-side fetch of every lead-linked email
  // (outbound + inbound). Complements `thread.email_threads` which
  // only surfaces emails that participated in a linked thread; direct
  // sends without a thread only appear via this endpoint. Fetched
  // lazily so the drawer's initial paint isn't slowed by a request the
  // operator may never need.
  useEffect(() => {
    if (tab !== 1 || !threadId || leadEmailsLoaded) return;
    let cancelled = false;
    fetchLeadEmails(threadId)
      .then((r) => { if (!cancelled) { setLeadEmails(r.data); setLeadEmailsLoaded(true); } })
      .catch(() => { if (!cancelled) { setLeadEmails([]); setLeadEmailsLoaded(true); } });
    return () => { cancelled = true; };
  }, [tab, threadId, leadEmailsLoaded]);

  // Deduped direct-list: any email that already appears inside a
  // grouped email_thread is filtered out so the same message never
  // renders twice. Key is `email.id` — not subject / recipient /
  // timestamp, which are not unique. See dedupeLeadEmails.test.ts.
  const directOnlyEmails = useMemo(
    () => dedupeLeadEmails(leadEmails, thread?.email_threads),
    [leadEmails, thread?.email_threads],
  );

  const handleUpdate = async (data: Partial<LeadThread>) => {
    if (!thread) return;
    setSaving(true);
    try {
      const updated = await updateLead(thread.id, data);
      setThread((prev) => (prev ? { ...prev, ...updated } : prev));
      onUpdated?.();
    } catch {}
    setSaving(false);
  };

  const handleAddNote = async () => {
    if (!thread || !noteText.trim()) return;
    try {
      await addLeadNote(thread.id, noteText.trim());
      setNoteText("");
      load();
    } catch {}
  };

  const handleDeleteNote = async (noteId: number) => {
    try {
      await deleteLeadNote(noteId);
      load();
    } catch {}
  };

  if (!open) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", md: DRAWER_WIDTH },
          bgcolor: "#fafafa",
          // Make the Paper itself a flex column so the header / body
          // can be properly sized without a fragile calc(100% - 80px).
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* ── Header (fixed at top — never scrolls) ─────────────────── */}
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: NAVY,
          color: "#fff",
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Avatar sx={{ bgcolor: GOLD, color: NAVY, width: 44, height: 44, fontWeight: 700 }}>
          {thread?.name?.charAt(0)?.toUpperCase() || "?"}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* INQUIRY identity — the lead's submitted name. Never replaced
              by the linked customer account's name. */}
          <Typography sx={{ fontSize: 9, fontWeight: 800, letterSpacing: 1, color: GOLD, textTransform: "uppercase", opacity: 0.85, lineHeight: 1 }}>
            Inquiry Contact
          </Typography>
          <Stack direction="row" alignItems="center" gap={1} mt={0.2}>
            <Typography variant="h6" noWrap sx={{ fontWeight: 600, fontSize: "1.05rem", textTransform: "capitalize" }}>
              {thread?.name?.toLowerCase() || "Loading..."}
            </Typography>
            {/* Public lead-tracking number (header variant — white-on-navy). */}
            {thread?.lead_number && (
              <LeadNumberBadge value={thread.lead_number} variant="header" />
            )}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" mt={0.4} flexWrap="wrap" useFlexGap>
            {thread && (() => {
              // Operational state derivations — kept aligned with the
              // leads table's ResponseBadge so badges never contradict.
              const isOverdue     = !!(thread.follow_up_required && thread.follow_up_at && new Date(thread.follow_up_at).getTime() < Date.now());
              const isNotContacted = thread.status === "new" && !thread.first_response_at;
              const isHot          = thread.ai_score === "hot";
              return (
                <>
                  <Chip
                    size="small"
                    label={STATUS_CONFIG[thread.status]?.label}
                    sx={{
                      bgcolor: STATUS_CONFIG[thread.status]?.color,
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "0.7rem",
                      height: 22,
                    }}
                  />
                  {/* Hot / Warm / Cold AI score — keep the existing chip so
                      the header still reads at a glance. */}
                  {thread.ai_score && (
                    <Chip
                      size="small"
                      label={`${AI_SCORE_CONFIG[thread.ai_score].icon} ${AI_SCORE_CONFIG[thread.ai_score].label}`}
                      sx={{
                        bgcolor: isHot ? "rgba(239,68,68,0.20)" : "rgba(255,255,255,0.15)",
                        color: "#fff",
                        fontSize: "0.7rem",
                        height: 22,
                        border: isHot ? "1px solid rgba(239,68,68,0.45)" : "1px solid rgba(255,255,255,0.18)",
                      }}
                    />
                  )}
                  {/* Operational urgency badges — only render when actually
                      true so the header doesn't carry noise. */}
                  {isOverdue && (
                    <Chip
                      size="small"
                      icon={<WarningIcon sx={{ color: "#fff !important", fontSize: 12 }} />}
                      label="Overdue"
                      sx={{
                        bgcolor: "#dc2626",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: "0.7rem",
                        height: 22,
                        letterSpacing: 0.3,
                      }}
                    />
                  )}
                  {isNotContacted && (
                    <Chip
                      size="small"
                      label="Not Contacted"
                      sx={{
                        bgcolor: "rgba(239,68,68,0.18)",
                        color: "#fff",
                        border: "1px solid rgba(239,68,68,0.45)",
                        fontWeight: 700,
                        fontSize: "0.7rem",
                        height: 22,
                        letterSpacing: 0.3,
                      }}
                    />
                  )}
                  {thread.is_vip && (
                    <Chip
                      size="small"
                      icon={<StarIcon sx={{ color: `${GOLD} !important`, fontSize: 14 }} />}
                      label="VIP"
                      sx={{
                        bgcolor: "rgba(201,169,110,0.2)",
                        color: GOLD,
                        fontSize: "0.7rem",
                        height: 22,
                      }}
                    />
                  )}
                </>
              );
            })()}
          </Stack>

          {/* ── Linked Customer pill — separate identity ─────────
              Shown only when a customer account is linked. Click opens
              the customer profile (drawer closes). */}
          {thread?.customer_id && thread.customer && (() => {
            const cust = thread.customer;
            const custName = cust.display_name?.trim() ?? "";
            const namesDiffer = custName.toLowerCase() !== (thread.name ?? "").trim().toLowerCase();
            return (
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                  navigate(`/customer-history/${thread.customer_id}`);
                }}
                sx={{
                  mt: 0.7, display: "inline-flex", alignItems: "center", gap: 0.7,
                  px: 1, py: 0.3, borderRadius: 1,
                  bgcolor: "rgba(201,168,76,0.14)",
                  border: `1px solid ${GOLD}40`,
                  cursor: "pointer",
                  transition: "all 0.15s",
                  maxWidth: "100%",
                  "&:hover": { bgcolor: "rgba(201,168,76,0.22)", borderColor: GOLD },
                }}
                title="Open linked customer profile"
              >
                <Typography sx={{ fontSize: 8.5, fontWeight: 800, letterSpacing: 0.7, color: GOLD, textTransform: "uppercase" }}>
                  Linked Customer
                </Typography>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#fff", textTransform: "capitalize" }} noWrap>
                  {custName.toLowerCase() || cust.phone || `#${cust.id}`}
                </Typography>
                {namesDiffer && (
                  <Box sx={{ fontSize: 8, fontWeight: 800, letterSpacing: 0.5, bgcolor: GOLD, color: NAVY, borderRadius: 0.4, px: 0.4, py: 0.05, textTransform: "uppercase" }}>
                    Diff name
                  </Box>
                )}
                {cust.phone && (
                  <Typography sx={{ fontSize: 10, color: "rgba(255,255,255,0.7)" }}>
                    📞 {cust.phone}
                  </Typography>
                )}
              </Box>
            );
          })()}
        </Box>
        <Tooltip title="Open full lead page">
          <IconButton
            onClick={() => { onClose(); navigate(`/leads/${threadId}`); }}
            sx={{ color: GOLD, "&:hover": { bgcolor: "rgba(201,169,110,0.2)" } }}
          >
            <OpenInNewIcon />
          </IconButton>
        </Tooltip>
        <IconButton onClick={onClose} sx={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: GOLD }} />
        </Box>
      ) : thread ? (
        // 3-region flex layout — fixes the prior bug where a tall CRM panel
        // pushed the tab strip out of the viewport.
        //   • Header (fixed above; flexShrink:0)
        //   • CRM region (bounded by maxHeight, scrolls internally)
        //   • Tab strip   (flexShrink:0 — always visible)
        //   • Tab content (flex:1 + minHeight:0 — scrolls inside its area)
        // minHeight:0 is the critical flex incantation that lets a child
        // shrink below its content height; without it the flexbox refuses
        // to clip and the overflow:auto on the inner Box never engages.
        <Box sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}>

          {/* ── Customer Relationship panel (bounded scroll region) ───
              Capped at ~42vh so it never dominates the drawer; if the
              customer has many related leads, this region scrolls
              independently and the tab strip below stays anchored. */}
          {relatedLeads.length > 0 && (
            <Box sx={{
              p: 1.5,
              bgcolor: "#f6f7fb",
              borderBottom: "1px solid #e2e8f0",
              flexShrink: 0,
              maxHeight: { xs: "38vh", sm: "42vh" },
              overflowY: "auto",
              overflowX: "hidden",
              // Subtle scrollbar so two parallel scroll regions don't
              // produce competing visual noise.
              "&::-webkit-scrollbar":        { width: 8 },
              "&::-webkit-scrollbar-track":  { bgcolor: "transparent" },
              "&::-webkit-scrollbar-thumb":  { bgcolor: "rgba(13,27,42,0.18)", borderRadius: 4 },
              "&:hover::-webkit-scrollbar-thumb": { bgcolor: "rgba(13,27,42,0.32)" },
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(13,27,42,0.22) transparent",
            }}>
              <CustomerRelationshipPanel
                currentLead={thread}
                relatedLeads={relatedLeads}
                canManageResolution={canManageResolution}
                density="compact"
                onNavigateLead={(id) => { onClose(); navigate(`/leads/${id}`); }}
                // Per-card actions: the panel passes the specific lead id.
                // Same handler signature, just now scoped to the card.
                onMarkPrimary={async (leadId) => {
                  try { await markLeadAsPrimary(leadId); await load(); onUpdated?.(); } catch {}
                }}
                onFlagSameEvent={async (childId, primaryId, reason) => {
                  try { await flagLeadSameEvent(childId, { primary_id: primaryId, reason }); await load(); onUpdated?.(); } catch {}
                }}
                onTrash={async (leadId) => {
                  try { await trashLead(leadId); await load(); onUpdated?.(); } catch {}
                }}
                onRestore={async (leadId) => {
                  try { await restoreLead(leadId); await load(); onUpdated?.(); } catch {}
                }}
                onOpenCustomer={(cid) => { onClose(); navigate(`/customer-history/${cid}`); }}
              />
            </Box>
          )}

          {/* ── Tabs (fixed strip — flexShrink:0 keeps them visible) ── */}
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              flexShrink: 0,
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "#fff",
              boxShadow: "0 1px 0 rgba(13,27,42,0.04)",
              "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 44 },
              "& .Mui-selected": { color: `${NAVY} !important` },
              "& .MuiTabs-indicator": { bgcolor: GOLD },
            }}
          >
            <Tab label="Details" />
            <Tab label="Emails" />
            <Tab label="Notes" />
            <Tab label="Timeline" />
            <Tab label="AI" />
          </Tabs>

          {/* Tab content — independently scrollable. minHeight:0 is what
              actually lets the overflow engage inside a flex column. */}
          <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 2.5 }}>
            {/* ── TAB 0: Details ───────────────────────────────────── */}
            {tab === 0 && (
              <Stack spacing={2.5}>
                {/* ─── Customer & Inquiry — unified identity card ───
                    Renders ONE card when the inquiry's submitted name /
                    phone / email match the linked customer's stable
                    identity (or when no customer is linked at all).
                    Only splits into a second "Inquiry Submitted Data"
                    block when at least one field differs — that's the
                    operationally interesting case (e.g. groom inquiring
                    on the bride's account). Field-level diff badges
                    surface exactly which value differs. */}
                {(() => {
                  const norm     = (s?: string | null) => (s ?? "").trim().toLowerCase();
                  const normPhn  = (s?: string | null) => (s ?? "").replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "");
                  const cust     = thread.customer_id ? thread.customer : null;
                  const custName = cust?.display_name?.trim() ?? "";

                  // Per-field equivalence (normalized) — drives diff badges.
                  const nameMatches  = !cust || norm(custName)   === norm(thread.name);
                  const phoneMatches = !cust || normPhn(cust.phone) === normPhn(thread.phone);
                  const emailMatches = !cust || norm(cust.email) === norm(thread.email);
                  const allMatch     = nameMatches && phoneMatches && emailMatches;

                  // Primary display: customer values when linked + match,
                  // otherwise inquiry values. Always falls back gracefully.
                  const showName  = cust && nameMatches  ? (custName || thread.name)   : (thread.name);
                  const showPhone = cust && phoneMatches ? (cust.phone || thread.phone) : (thread.phone);
                  const showEmail = cust && emailMatches ? (cust.email || thread.email) : (thread.email);

                  return (
                    <Paper elevation={0} sx={{ p: 2.5, border: "1px solid #e5e7eb", borderRadius: 2 }}>
                      <Stack direction="row" alignItems="center" gap={1} mb={1.5} flexWrap="wrap" useFlexGap>
                        <Typography variant="subtitle2" sx={{ color: NAVY, fontWeight: 700 }}>
                          Customer & Inquiry
                        </Typography>
                        {cust && (
                          <Box sx={{
                            display: "inline-flex", alignItems: "center", gap: 0.4,
                            fontSize: 9, fontWeight: 800, letterSpacing: 0.5,
                            bgcolor: `${GOLD}22`, color: GOLD,
                            border: `1px solid ${GOLD}55`, borderRadius: 0.6,
                            px: 0.6, py: 0.1, textTransform: "uppercase",
                          }}>
                            ★ Linked Customer
                          </Box>
                        )}
                        {cust && !allMatch && (
                          <Box sx={{
                            fontSize: 9, fontWeight: 800, letterSpacing: 0.5,
                            bgcolor: GOLD, color: NAVY, borderRadius: 0.6,
                            px: 0.6, py: 0.1, textTransform: "uppercase",
                          }}>
                            Inquiry differs
                          </Box>
                        )}
                        <Box flex={1} />
                        {cust && (
                          <Typography
                            variant="caption"
                            onClick={() => { onClose(); navigate(`/customer-history/${cust.id}`); }}
                            sx={{
                              fontSize: 11, fontWeight: 700, color: GOLD,
                              cursor: "pointer", textTransform: "uppercase", letterSpacing: 0.5,
                              "&:hover": { color: NAVY },
                            }}
                          >
                            Open profile →
                          </Typography>
                        )}
                      </Stack>

                      <Stack spacing={1.5}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Box sx={{ color: "#94a3b8", display: "flex" }}><PersonIcon /></Box>
                          <Typography variant="body2" sx={{ color: "#64748b", minWidth: 100 }}>Name</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: NAVY, textTransform: "capitalize" }}>
                            {showName?.toLowerCase() || "—"}
                          </Typography>
                        </Stack>
                        <InfoRow icon={<EmailIcon />} label="Email" value={showEmail} />
                        <InfoRow icon={<PhoneIcon />} label="Phone" value={showPhone} />
                        <InfoRow icon={<BusinessIcon />} label="Organization" value={thread.organization} />
                      </Stack>

                      {/* Inquiry-submitted-data sub-block — only renders
                          when one or more fields differ from the linked
                          customer. Operators immediately see the bride-
                          vs-groom-vs-planner discrepancies that drive
                          customer intelligence. */}
                      {cust && !allMatch && (
                        <Box sx={{
                          mt: 1.8, pt: 1.4,
                          borderTop: "1px dashed #e5e7eb",
                        }}>
                          <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.8, color: "#94a3b8", textTransform: "uppercase", mb: 0.8 }}>
                            Inquiry Submitted Data
                          </Typography>
                          <Stack spacing={0.8}>
                            {!nameMatches && (
                              <Stack direction="row" alignItems="center" gap={1}>
                                <Typography sx={{ fontSize: 11, color: "#94a3b8", minWidth: 70 }}>Name</Typography>
                                <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: NAVY, textTransform: "capitalize" }}>
                                  {thread.name?.toLowerCase() || "—"}
                                </Typography>
                              </Stack>
                            )}
                            {!phoneMatches && (
                              <Stack direction="row" alignItems="center" gap={1}>
                                <Typography sx={{ fontSize: 11, color: "#94a3b8", minWidth: 70 }}>Phone</Typography>
                                <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: NAVY, fontVariantNumeric: "tabular-nums" }}>
                                  {thread.phone || "—"}
                                </Typography>
                              </Stack>
                            )}
                            {!emailMatches && (
                              <Stack direction="row" alignItems="center" gap={1}>
                                <Typography sx={{ fontSize: 11, color: "#94a3b8", minWidth: 70 }}>Email</Typography>
                                <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: NAVY }}>
                                  {thread.email || "—"}
                                </Typography>
                              </Stack>
                            )}
                          </Stack>
                        </Box>
                      )}
                    </Paper>
                  );
                })()}

                {/* Program / Inquiry Details — vertical-aware.
                    Autism/tutoring: pulls program, location, grade/age,
                    preferred schedule, preferred start from the normalized
                    lead_inquiries.custom_fields blob and renders only the
                    rows that carry a value (empty rows are hidden so the
                    drawer never fills with "Not specified").
                    Venue (DCC): keeps the original Event Type / Date /
                    Guests / Preferred Hall / Budget layout. */}
                {(() => {
                  const subject = resolveInquirySubject(thread);
                  const rows: Array<{ icon: React.ReactNode; label: string; value: string | null | undefined }> = [];

                  if (leadConfig.supportsEventFields) {
                    rows.push({ icon: <EventIcon  />, label: "Event Type",     value: thread.event_type });
                    if (showsVenueField("event_date"))
                      rows.push({ icon: <EventIcon  />, label: "Event Date",   value: formatEventDate(thread.event_date) });
                    if (showsVenueField("guest_count"))
                      rows.push({ icon: <PeopleIcon />, label: "Guest Count",  value: thread.guest_count?.toString() });
                    if (showsVenueField("hall"))
                      rows.push({ icon: <HallIcon   />, label: "Preferred Hall", value: thread.preferred_hall });
                    if (showsVenueField("budget"))
                      rows.push({ icon: <MoneyIcon  />, label: "Budget",       value: formatLeadBudget(thread) });
                  } else {
                    // Autism / tutoring — only render fields with real data.
                    if (subject.program)     rows.push({ icon: <EventIcon />,   label: "Program",         value: subject.program });
                    if (subject.location)    rows.push({ icon: <HallIcon />,    label: "Location",        value: subject.location });
                    if (subject.grade)       rows.push({ icon: <PeopleIcon />,  label: "Grade / Level",   value: subject.grade });
                    else if (subject.age)    rows.push({ icon: <PeopleIcon />,  label: "Age",             value: subject.age });
                    if (subject.schedule)    rows.push({ icon: <EventIcon />,   label: "Preferred Days",  value: subject.schedule });
                    if (subject.startPeriod) rows.push({ icon: <EventIcon />,   label: "Preferred Start", value: subject.startPeriod });
                  }

                  if (rows.length === 0) return null;

                  return (
                    <Paper elevation={0} sx={{ p: 2.5, border: "1px solid #e5e7eb", borderRadius: 2 }}>
                      <Typography variant="subtitle2" sx={{ color: NAVY, fontWeight: 700, mb: 1.5 }}>
                        {leadConfig.inquirySubjectSectionTitle}
                      </Typography>
                      <Stack spacing={1.5}>
                        {rows.map((r, i) => (
                          <InfoRow key={i} icon={r.icon} label={r.label} value={r.value} />
                        ))}
                      </Stack>
                    </Paper>
                  );
                })()}

                {/* Inquiry Message */}
                {thread.inquiry?.message && (
                  <Paper elevation={0} sx={{ p: 2.5, border: "1px solid #e5e7eb", borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: NAVY, fontWeight: 700, mb: 1 }}>
                      Inquiry Message
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#475569", whiteSpace: "pre-wrap" }}>
                      {thread.inquiry.message}
                    </Typography>
                  </Paper>
                )}

                {/* ─── Lead Actions ────────────────────────────────
                    Unified action panel — merges the prior "Actions"
                    card (status / priority / assignee / VIP) with the
                    "Contact Log" form. One LOG ACTIVITY click sends the
                    whole change-set; the backend bundles every emitted
                    activity row under a single request_id so they group
                    into ONE Timeline card. Identical to the panel on
                    the full Lead Detail page so reps see the same flow
                    no matter where they're triaging from. */}
                <Paper elevation={0} sx={{ p: 2.5, border: "1px solid #e5e7eb", borderRadius: 2 }}>
                  <ContactLogPanel
                    lead={thread}
                    teamUsers={teamUsers}
                    variant="compact"
                    onLogged={() => load()}
                  />
                </Paper>

                {/* ─── Timeline ─────────────────────────────────────
                    Preview of the lead's full activity history — paired
                    visually with Contact Log above to realize the
                    standard ACTION → HISTORY flow.

                    Renders the SHARED <LeadActivityTimeline /> component
                    in "preview" mode (filter tabs hidden, capped at 5
                    grouped cards, "view full timeline" link routes to
                    the dedicated Timeline tab). The same component
                    powers the Timeline tab and the lead detail page —
                    so avatars / role badges / NoteBubbles / FieldDiffs /
                    outcome badges / request-id grouping / date buckets
                    all match across surfaces, and any future change in
                    one shows up in both. */}
                <Paper elevation={0} sx={{ p: 2.5, border: "1px solid #e5e7eb", borderRadius: 2 }}>
                  <Stack direction="row" alignItems="flex-start" gap={1} mb={1.2}>
                    <Box flex={1} minWidth={0}>
                      <Typography sx={{ fontSize: 13, fontWeight: 800, color: NAVY, letterSpacing: 0.2 }}>
                        Timeline
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.1 }}>
                        Complete lead history and staff actions
                      </Typography>
                    </Box>
                    <Typography
                      onClick={() => setTab(3)}
                      sx={{
                        fontSize: 11, fontWeight: 700, color: GOLD,
                        cursor: "pointer", textTransform: "uppercase", letterSpacing: 0.5,
                        "&:hover": { color: NAVY },
                      }}
                    >
                      Full timeline →
                    </Typography>
                  </Stack>
                  {threadId && (
                    <LeadActivityTimeline
                      threadId={threadId}
                      mode="preview"
                      previewLimit={5}
                      onShowMore={() => setTab(3)}
                    />
                  )}
                </Paper>

                {/* Source & Meta */}
                <Paper elevation={0} sx={{ p: 2.5, border: "1px solid #e5e7eb", borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: NAVY, fontWeight: 700, mb: 1 }}>
                    Source & Tracking
                  </Typography>
                  <Stack spacing={1}>
                    <InfoRow icon={<FlagIcon />} label="Source" value={thread.source} />
                    <InfoRow
                      icon={<HistoryIcon />}
                      label="Created"
                      value={formatDateTime(thread.created_at)}
                    />
                    {thread.first_response_at && (
                      <InfoRow
                        icon={<HistoryIcon />}
                        label="First Response"
                        value={formatDateTime(thread.first_response_at)}
                      />
                    )}
                    {thread.response_delay_minutes != null && (
                      <InfoRow
                        icon={<HistoryIcon />}
                        label="Response Time"
                        value={`${thread.response_delay_minutes} min`}
                      />
                    )}
                  </Stack>
                </Paper>
              </Stack>
            )}

            {/* ── TAB 1: Emails ────────────────────────────────────── */}
            {tab === 1 && (
              <Stack spacing={2}>
                {/* Direct sends + full email history — sourced from
                    GET /leads/{id}/emails so row-action Send Email
                    entries surface here even without an email_thread. */}
                {directOnlyEmails.length > 0 && (
                  <Paper elevation={0} sx={{ p: 2, border: "1px solid #e5e7eb", borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Direct emails ({directOnlyEmails.length})
                    </Typography>
                    <Stack divider={<Divider flexItem />} spacing={1}>
                      {directOnlyEmails.map((e) => (
                        <Box key={e.id}>
                          <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                            <Typography variant="caption" sx={{ fontWeight: 700, color: NAVY }}>
                              {e.direction === "outgoing" ? "→" : "←"} {e.subject || "(no subject)"}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                              {e.sent_at ? formatDateTime(e.sent_at) : ""}
                            </Typography>
                          </Stack>
                          <Typography variant="caption" sx={{ color: "#64748b", display: "block" }}>
                            {e.direction === "outgoing"
                              ? <>To: {e.to.join(", ") || "—"}</>
                              : <>From: {e.from_name || e.from_email || "—"}</>}
                            {e.status && <> &middot; {e.status}</>}
                          </Typography>
                          {e.body_preview && (
                            <Typography variant="body2" sx={{ mt: 0.5, color: "#475569", fontSize: "0.8rem" }}>
                              {e.body_preview}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                )}
                {thread.email_threads && thread.email_threads.length > 0 ? (
                  thread.email_threads.map((et) => (
                    <Paper
                      key={et.id}
                      elevation={0}
                      sx={{
                        p: 2,
                        border: "1px solid #e5e7eb",
                        borderRadius: 2,
                        cursor: "pointer",
                        "&:hover": { borderColor: GOLD },
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {et.subject}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#64748b" }}>
                            {et.customer_email} &middot;{" "}
                            {et.last_message_at
                              ? formatShortDate(et.last_message_at)
                              : ""}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={0.5}>
                          {et.unread_count > 0 && (
                            <Badge badgeContent={et.unread_count} color="primary" />
                          )}
                          <Chip
                            size="small"
                            label="Linked"
                            sx={{
                              bgcolor: "#dcfce7",
                              color: "#16a34a",
                              fontSize: "0.65rem",
                              height: 20,
                            }}
                          />
                          {et.is_private && (
                            <Chip
                              size="small"
                              label="Private"
                              sx={{
                                bgcolor: "#fef2f2",
                                color: "#ef4444",
                                fontSize: "0.65rem",
                                height: 20,
                              }}
                            />
                          )}
                        </Stack>
                      </Stack>
                      {/* Show email messages inline */}
                      {et.emails && et.emails.length > 0 && (
                        <Box sx={{ mt: 1.5, pl: 1, borderLeft: `2px solid ${GOLD}` }}>
                          {et.emails.slice(-3).map((em: any) => (
                            <Box key={em.id} sx={{ mb: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: NAVY }}>
                                {em.from_name || em.from_email}
                                <span style={{ fontWeight: 400, color: "#94a3b8", marginLeft: 8 }}>
                                  {formatDateTime(em.received_at)}
                                </span>
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ color: "#475569", fontSize: "0.8rem" }}
                              >
                                {em.body_preview || ""}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Paper>
                  ))
                ) : directOnlyEmails.length === 0 && leadEmailsLoaded ? (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    No emails linked to this lead yet. Send one from the row action menu or link a thread from the Email Hub.
                  </Alert>
                ) : null}
              </Stack>
            )}

            {/* ── TAB 2: Notes ─────────────────────────────────────── */}
            {tab === 2 && (
              <Stack spacing={2}>
                <Paper elevation={0} sx={{ p: 2, border: "1px solid #e5e7eb", borderRadius: 2 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Add an internal note..."
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    size="small"
                    sx={{ mb: 1 }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SendIcon />}
                    onClick={handleAddNote}
                    disabled={!noteText.trim()}
                    sx={{ bgcolor: NAVY, "&:hover": { bgcolor: "#1b2e44" } }}
                  >
                    Add Note
                  </Button>
                </Paper>

                {thread.lead_notes?.map((n) => (
                  <Paper
                    key={n.id}
                    elevation={0}
                    sx={{ p: 2, border: "1px solid #e5e7eb", borderRadius: 2 }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                          {n.note}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#94a3b8", mt: 0.5 }}>
                          {formatDateTime(n.created_at)}
                        </Typography>
                      </Box>
                      <IconButton size="small" onClick={() => handleDeleteNote(n.id)}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Stack>
                  </Paper>
                ))}

                {(!thread.lead_notes || thread.lead_notes.length === 0) && (
                  <Typography variant="body2" sx={{ color: "#94a3b8", textAlign: "center", py: 4 }}>
                    No notes yet
                  </Typography>
                )}
              </Stack>
            )}

            {/* ── TAB 3: Timeline ──────────────────────────────────── */}
            {tab === 3 && threadId && (
              <LeadActivityTimeline threadId={threadId} />
            )}

            {/* ── TAB 4: AI Suggestions ────────────────────────────── */}
            {tab === 4 && (
              <Stack spacing={2}>
                {suggestions.length > 0 ? (
                  suggestions.map((s, i) => (
                    <Paper
                      key={i}
                      elevation={0}
                      sx={{
                        p: 2.5,
                        border: `1px solid ${NAVY}22`,
                        borderRadius: 2,
                        bgcolor: "#f5f8ff",
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <AiIcon sx={{ color: NAVY, fontSize: 22, mt: 0.3 }} />
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: NAVY }}>
                            {s.title}
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 600, color: NAVY, my: 0.5 }}
                          >
                            {s.value}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#64748b" }}>
                            {s.reason}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  ))
                ) : (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    No AI suggestions available for this lead yet.
                  </Alert>
                )}
              </Stack>
            )}
          </Box>
        </Box>
      ) : loadError ? (
        // Real error surfacing — distinguishes "API failed" from "no such id".
        // The previous catch{} would silently fall through to "Lead not
        // found" even when the backend returned a 500.
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography color="error" fontWeight={700}>Couldn't load this lead</Typography>
          <Typography color="text.secondary" variant="caption" sx={{ display: "block", mt: 0.5, mb: 2 }}>
            {loadError}
          </Typography>
          <Button size="small" variant="outlined" onClick={load} sx={{ textTransform: "none" }}>
            Retry
          </Button>
        </Box>
      ) : (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">Lead not found</Typography>
        </Box>
      )}
    </Drawer>
  );
}

/* ── Helper Components ────────────────────────────────────────────────── */

function InfoRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | null | undefined;
}) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box sx={{ color: "#94a3b8", display: "flex" }}>{icon}</Box>
      <Typography variant="body2" sx={{ color: "#64748b", minWidth: 100 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, color: "#1e293b" }}>
        {value || "—"}
      </Typography>
    </Stack>
  );
}

/* The activityVisuals / activityNotePreview helpers that used to live
   here were removed when the drawer's compact timeline preview was
   migrated to the shared <LeadActivityTimeline mode="preview" />
   component. All timeline rendering (avatars, role badges, NoteBubbles,
   FieldDiffs, outcome chips, request-id grouping, date buckets) is now
   centralized in LeadActivityTimeline so the drawer and full timeline
   stay visually identical. */

/* ────────────────────────────────────────────────────────────────────────
 * Customer Status panel — reflects the new always-create + customer-link
 * workflow. Sits above the drawer tabs and surfaces:
 *   • Existing-customer / Related-enquiries / Primary-event badges
 *   • The list of other leads on the same customer (lead#, event, status)
 *   • Operator actions (Mark Primary · Flag Same Event · Trash · Restore)
 *
 * Never reintroduces merge semantics — every related lead remains a
 * distinct record with its own audit trail.
 * ──────────────────────────────────────────────────────────────────────── */
function CustomerStatusPanel({
  currentLead,
  relatedLeads,
  canManageResolution,
  onNavigateLead,
  onMarkPrimary,
  onFlagSameEvent,
  onTrash,
  onRestore,
}: {
  currentLead: LeadThread;
  relatedLeads: LeadThread[];
  canManageResolution: boolean;
  onNavigateLead: (id: number) => void;
  onMarkPrimary: () => void | Promise<void>;
  onFlagSameEvent: (primaryId: number, reason?: string) => void | Promise<void>;
  onTrash: () => void | Promise<void>;
  onRestore: () => void | Promise<void>;
}) {
  const [flagOpen, setFlagOpen] = useState(false);
  const [flagPrimary, setFlagPrimary] = useState<number | "">("");

  // Returning-customer signal: any lead on this customer older than this one.
  const isReturning = relatedLeads.some(
    (l) => new Date(l.created_at).getTime() < new Date(currentLead.created_at).getTime()
  );
  const activeRelated = relatedLeads.filter((l) => !l.archived_at && !l.merged_into_lead_id);
  const isFinal = !!currentLead.archived_at || !!currentLead.merged_into_lead_id;

  return (
    <Box sx={{ px: 2.5, py: 1.5, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
      {/* Badges row */}
      <Stack direction="row" gap={0.6} alignItems="center" flexWrap="wrap" mb={1}>
        <Tooltip title="This customer has prior enquiries on file">
          <Box sx={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5, bgcolor: "#fef3c7", color: "#b45309", border: "1px solid #fde68a", borderRadius: 0.8, px: 0.7, py: 0.1, textTransform: "uppercase" }}>
            ⚠ Existing Customer
          </Box>
        </Tooltip>
        {isReturning && (
          <Box sx={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5, bgcolor: "#dbeafe", color: "#1d4ed8", border: "1px solid #bfdbfe", borderRadius: 0.8, px: 0.7, py: 0.1, textTransform: "uppercase" }}>
            Returning Customer
          </Box>
        )}
        {activeRelated.length > 0 && (
          <Box sx={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5, bgcolor: "#ede9fe", color: "#6d28d9", border: "1px solid #ddd6fe", borderRadius: 0.8, px: 0.7, py: 0.1, textTransform: "uppercase" }}>
            ⚠ {activeRelated.length} Related Enquir{activeRelated.length === 1 ? "y" : "ies"}
          </Box>
        )}
        {currentLead.is_primary_event && (
          <Box sx={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5, bgcolor: "#fef3c7", color: "#b45309", border: "1px solid #fde68a", borderRadius: 0.8, px: 0.7, py: 0.1, textTransform: "uppercase" }}>
            ★ Primary Event
          </Box>
        )}
        {currentLead.resolution_status === "same_event_duplicate" && (
          <Box sx={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5, bgcolor: "#ede9fe", color: "#6d28d9", border: "1px solid #ddd6fe", borderRadius: 0.8, px: 0.7, py: 0.1, textTransform: "uppercase" }}>
            🔁 Same Event
          </Box>
        )}
        {currentLead.archived_at && (
          <Box sx={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5, bgcolor: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 0.8, px: 0.7, py: 0.1, textTransform: "uppercase" }}>
            🗂 {currentLead.resolution_status === "trash" ? "Trashed" : "Archived"}
          </Box>
        )}
      </Stack>

      {/* Related leads list */}
      <Stack gap={0.5} mb={canManageResolution ? 1 : 0}>
        {relatedLeads.slice(0, 6).map((l) => {
          const final = !!l.archived_at || !!l.merged_into_lead_id;
          const statusCfg = STATUS_CONFIG[l.status] || { label: l.status, color: "#94a3b8" };
          return (
            <Box
              key={l.id}
              onClick={() => onNavigateLead(l.id)}
              sx={{
                p: 0.7, borderRadius: 1.2, cursor: "pointer",
                bgcolor: "#fff", border: "1px solid #e2e8f0",
                opacity: final ? 0.7 : 1,
                "&:hover": { borderColor: NAVY, bgcolor: "#f1f5f9" },
              }}
            >
              <Stack direction="row" alignItems="center" gap={0.6} flexWrap="wrap">
                <Typography variant="caption" sx={{ fontFamily: "'SF Mono','Roboto Mono',Menlo,monospace", fontWeight: 700, fontSize: 11, color: NAVY, letterSpacing: 0.3 }}>
                  {l.lead_number ?? `#${l.id}`}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: 11.5, fontWeight: 600, color: "#1e293b", textTransform: "capitalize" }}>
                  {l.name}
                </Typography>
                <Box flex={1} />
                {l.is_primary_event && (
                  <Box sx={{ fontSize: 9, fontWeight: 800, bgcolor: "#fef3c7", color: "#b45309", border: "1px solid #fde68a", borderRadius: 0.6, px: 0.5, py: 0.05 }}>★ PRIMARY</Box>
                )}
                {l.resolution_status === "same_event_duplicate" && (
                  <Box sx={{ fontSize: 9, fontWeight: 800, bgcolor: "#ede9fe", color: "#6d28d9", border: "1px solid #ddd6fe", borderRadius: 0.6, px: 0.5, py: 0.05 }}>SAME EVENT</Box>
                )}
                {l.resolution_status === "trash" && (
                  <Box sx={{ fontSize: 9, fontWeight: 800, bgcolor: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 0.6, px: 0.5, py: 0.05 }}>TRASHED</Box>
                )}
                {!final && (
                  <Box sx={{ fontSize: 9, fontWeight: 800, bgcolor: `${statusCfg.color}22`, color: statusCfg.color, border: `1px solid ${statusCfg.color}40`, borderRadius: 0.6, px: 0.5, py: 0.05, textTransform: "uppercase" }}>
                    {statusCfg.label}
                  </Box>
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5, display: "block", mt: 0.2 }}>
                {(() => {
                  const summary = resolveInquirySubject(l);
                  const parts: string[] = [];
                  if (leadConfig.supportsEventFields) {
                    parts.push(l.event_type ? l.event_type.replace(/_/g, " ") : "—");
                    if (showsVenueField("event_date")) parts.push(l.event_date ?? "no date");
                  } else {
                    if (summary.program)  parts.push(summary.program);
                    if (summary.location) parts.push(summary.location);
                  }
                  return parts.length ? parts.join(" · ") : "—";
                })()}
              </Typography>
            </Box>
          );
        })}
      </Stack>

      {/* Operator actions — visible, not hidden behind a kebab menu */}
      {canManageResolution && (
        <Stack direction="row" gap={0.6} flexWrap="wrap">
          {!isFinal && !currentLead.is_primary_event && (
            <Button size="small" variant="outlined" onClick={() => onMarkPrimary()}
              sx={{ textTransform: "none", fontSize: 11, fontWeight: 700, py: 0.2, borderColor: "#fde68a", color: "#b45309", "&:hover": { bgcolor: "#fffbeb", borderColor: "#b45309" } }}>
              ★ Mark Primary
            </Button>
          )}
          {!isFinal && activeRelated.length > 0 && (
            <Button size="small" variant="outlined" onClick={() => { setFlagPrimary(""); setFlagOpen(true); }}
              sx={{ textTransform: "none", fontSize: 11, fontWeight: 700, py: 0.2, borderColor: "#ddd6fe", color: "#6d28d9", "&:hover": { bgcolor: "#f5f3ff", borderColor: "#6d28d9" } }}>
              🔁 Flag Same Event
            </Button>
          )}
          {!isFinal && (
            <Button size="small" variant="outlined" onClick={() => onTrash()}
              sx={{ textTransform: "none", fontSize: 11, fontWeight: 700, py: 0.2, borderColor: "#e2e8f0", color: "#475569", "&:hover": { bgcolor: "#f1f5f9", borderColor: "#94a3b8" } }}>
              🗑 Move to Trash
            </Button>
          )}
          {isFinal && (
            <Button size="small" variant="outlined" onClick={() => onRestore()}
              sx={{ textTransform: "none", fontSize: 11, fontWeight: 700, py: 0.2, borderColor: "#bbf7d0", color: "#16a34a", "&:hover": { bgcolor: "#f0fdf4", borderColor: "#16a34a" } }}>
              ↺ Restore Lead
            </Button>
          )}
        </Stack>
      )}

      {/* Flag same-event picker — inline mini dialog */}
      <Dialog open={flagOpen} onClose={() => setFlagOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: NAVY, fontSize: 16 }}>Flag as same event</DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, fontSize: 11.5 }}>
            Pick the PRIMARY lead this enquiry belongs to. Lead <b>{currentLead.lead_number ?? `#${currentLead.id}`}</b> will be hidden from active pipeline as a same-event duplicate — history preserved, can be restored.
          </Typography>
          <Stack gap={0.5}>
            {activeRelated.map((l) => (
              <Box
                key={l.id}
                onClick={() => setFlagPrimary(l.id)}
                sx={{
                  p: 0.8, borderRadius: 1.2, cursor: "pointer",
                  bgcolor: flagPrimary === l.id ? "#ede9fe" : "#fff",
                  border: `1.5px solid ${flagPrimary === l.id ? "#6d28d9" : "#e2e8f0"}`,
                  "&:hover": { borderColor: "#6d28d9" },
                }}
              >
                <Typography variant="caption" sx={{ fontFamily: "'SF Mono','Roboto Mono',Menlo,monospace", fontWeight: 700, fontSize: 11, color: NAVY }}>
                  {l.lead_number ?? `#${l.id}`}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: 11.5, ml: 0.6 }}>
                  {l.name}
                  {(() => {
                    const s = resolveInquirySubject(l);
                    const bits: string[] = [];
                    if (leadConfig.supportsEventFields) {
                      bits.push(l.event_type ?? "—");
                      if (showsVenueField("event_date")) bits.push(l.event_date ?? "no date");
                    } else if (s.program) {
                      bits.push(s.program);
                    }
                    return bits.length ? ` · ${bits.join(" · ")}` : "";
                  })()}
                  {l.is_primary_event && " · ★"}
                </Typography>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFlagOpen(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!flagPrimary}
            onClick={async () => {
              if (typeof flagPrimary === "number") {
                await onFlagSameEvent(flagPrimary);
                setFlagOpen(false);
              }
            }}
            sx={{ textTransform: "none", bgcolor: "#6d28d9", "&:hover": { bgcolor: "#5b21b6" } }}
          >
            Flag Same Event
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

